import { MODEL_SERVICE_URL } from './config';
import { getToken as getAuthToken } from '../stores/authStore';
import { refreshAccessToken } from './tokenRefresh';
import { sanitizeErrorMessage } from './errorMessages';

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshComplete(newToken) {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
}

// Wake queued callers with the reason renewal failed, so a rate-limited or
// briefly unreachable renewal reads as "try again" rather than "signed out".
function onRefreshFailed(error) {
  refreshSubscribers.forEach(cb => cb(null, error));
  refreshSubscribers = [];
}

function buildRequest(url, options, token) {
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
  return { url, init: { ...options, headers } };
}

/**
 * Make a fetch call to the model service with the user's JWT token.
 * On 401, refreshes the access token once and retries the request before
 * returning to the caller.
 */
// Wrap fetch so the error thrown to callers never carries the raw URL/host.
// We log the original error to the console for developer debugging.
async function safeFetch(url, init) {
  try {
    return await fetch(url, init);
  } catch (err) {
    console.error('modelServiceFetch network error:', err);
    throw new Error(sanitizeErrorMessage(err));
  }
}

export async function modelServiceFetch(path, options = {}) {
  const url = `${MODEL_SERVICE_URL}${path}`;
  const token = getAuthToken();
  const { init } = buildRequest(url, options, token);

  let response = await safeFetch(url, init);
  if (response.status !== 401) return response;

  // 401 — attempt a single refresh + retry.
  if (isRefreshing) {
    // A refresh is already in flight; queue up and retry when it resolves.
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh((newToken, refreshError) => {
        if (!newToken) {
          reject(refreshError || new Error('Session expired. Please login again.'));
          return;
        }
        const { init: retryInit } = buildRequest(url, options, newToken);
        resolve(safeFetch(url, retryInit));
      });
    });
  }

  isRefreshing = true;
  try {
    let newToken;
    try {
      newToken = await refreshAccessToken();
    } catch (refreshError) {
      // Transient renewal failure (rate limited, or the API was briefly
      // unreachable). Nothing says the session ended, so hand back the original
      // 401 for the caller to react to instead of tearing the session down.
      onRefreshFailed(refreshError);
      return response;
    }
    if (!newToken) {
      onRefreshComplete(null);
      return response; // return the original 401 so callers can react
    }
    onRefreshComplete(newToken);
    const { init: retryInit } = buildRequest(url, options, newToken);
    response = await safeFetch(url, retryInit);
  } finally {
    isRefreshing = false;
  }
  return response;
}

/**
 * Convenience: POST JSON to model service.
 */
export async function modelServicePost(path, body, options = {}) {
  return modelServiceFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    body: JSON.stringify(body),
    ...options,
  });
}

/**
 * Convenience: POST FormData (file upload) to model service.
 */
export async function modelServiceUpload(path, formData, options = {}) {
  return modelServiceFetch(path, {
    method: 'POST',
    body: formData,
    ...options,
  });
}

/**
 * Clear model service state (call on logout). No-op since we use the JWT directly.
 */
export function clearModelServiceKey() {
  // Nothing to clear — the JWT is managed by AuthContext
}
